/**
 * 하위 항목 묶음 — 세로 가이드 라인 + 항목마다 ㄴ자(elbow) 연결선.
 *
 * <p>각 항목의 세로 중앙으로 가지가 곡선으로 이어지고, <b>마지막 항목에서는 세로 라인이
 * 가지 지점에서 끝난다</b>(그래서 묶음이 어디서 닫히는지가 보인다).
 *
 * <h3>왜 공통 컴포넌트인가</h3>
 * <p>원래 {@code OnandmeSidebar} 안의 private 함수였는데, 권한 트리(도메인 › 탭 › 세부 권한)도
 * 같은 계층 표현이 필요해졌다. 들여쓰기만 흉내 내면 "자식인지 형제인지" 가 안 보여서,
 * 실제로 세부 권한이 부모 탭과 같은 x 에 놓여 있어도 아무도 눈치채지 못했다.
 * 연결선은 그 관계를 <b>선으로</b> 못박으므로 들여쓰기 폭이 작아도 계층이 읽힌다.
 *
 * <h3>쓰는 법</h3>
 * <pre>
 *   부모 행
 *   &lt;Collapse in={open}&gt;
 *     &lt;SubTreeLine inset="10px"&gt;
 *       {children.map(c =&gt; &lt;Row key={c.id} … /&gt;)}
 *     &lt;/SubTreeLine&gt;
 *   &lt;/Collapse&gt;
 * </pre>
 *
 * <p>children 은 <b>항목 하나당 엘리먼트 하나</b>여야 한다 — 이 컴포넌트가 각 child 를 감싸서
 * 연결선을 그리므로, 여러 항목을 하나의 Fragment 로 묶어 넘기면 그 묶음 전체에 가지가 하나만 붙는다.
 * 펼침 영역(Collapse)까지 포함한 하위 트리를 child 하나로 넘기면 세로 라인이 그 높이만큼 이어진다.
 */
import React from "react";
import { Box } from "@mui/material";

export const TREE_LINE = "1.5px solid rgba(15,23,42,0.14)";

/** 가지가 항목 세로 중앙에 닿도록 하는 높이 — 행 높이가 크게 다르면 조정한다. */
const ELBOW_H = 14;

/**
 * @param inset 세로 라인의 좌측 오프셋.
 *   사이드바 1depth 기본값 31px = 섹션 아이콘의 가로 정중앙
 *   (Stack px 4 제외: mx 6 + px 12 + 아이콘 28/2 − 선폭 보정).
 *   중첩 단계는 부모 글자 아래로 살짝 들여쓴 10px — 부모의 아이콘/화살표 중앙쯤에 오면 자연스럽다.
 */
export default function SubTreeLine({ children, inset = "31px" }) {
    const items = React.Children.toArray(children);
    return (
        <Box sx={{ ml: inset }}>
            {items.map((child, i) => (
                <Box key={child.key ?? i} sx={{
                    position: "relative",
                    pl: "14px",
                    // ㄴ자 가지 — 행 세로 중앙까지 내려와 곡선으로 꺾여 항목에 닿는다
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        left: 0, top: 0,
                        width: 11, height: ELBOW_H,
                        borderLeft: TREE_LINE,
                        borderBottom: TREE_LINE,
                        borderBottomLeftRadius: 8,
                    },
                    // 마지막 항목이 아니면 세로 라인을 아래로 계속 잇는다
                    "&::after": i < items.length - 1 ? {
                        content: '""',
                        position: "absolute",
                        left: 0, top: 0, bottom: 0,
                        borderLeft: TREE_LINE,
                    } : undefined,
                }}>
                    {child}
                </Box>
            ))}
        </Box>
    );
}
